import { Box, Card, CircularProgress, type SxProps, type Theme, Typography } from '@mui/material'
import { ProgressReportCard } from './ProgressReportCard'
import { useNavigate, useParams } from 'react-router-dom'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { GetSurvey } from '../../../../services/SurveyService'
import { SurveyType } from '../../../../models/SurveySchema'
import { GetProgressReportDates } from '../../../../services/SystemConfigurationService'
import { useEffect, useState } from 'react'
import { useProgressReportAccess } from '../../../../hooks/use-progressReportAccess'

const styles: Record<string, SxProps<Theme>> = {
  card: {
    backgroundColor: 'var(--grey-100)',
    borderRadius: '12px',
    padding: '20px 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    position: 'relative',
    overflow: 'visible'
  },
  columnBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em'
  }
}

export const OrgProgressReports = (): JSX.Element => {
  const { planOrgId, planId, rcnId } = useParams()
  const planOrgIdNum = Number(planOrgId)
  const annualPlanId = Number(planId)
  const rcnIdNum = Number(rcnId)

  const navigate = useNavigate()

  const [isMidYearSubmitted, setIsMidYearSubmitted] = useState<boolean>(false)
  const [isEoySubmitted, setIsEoySubmitted] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [midYearDueDate, setMidYearDueDate] = useState<string>('')
  const [endOfYearDueDate, setEndOfYearDueDate] = useState<string>('')

  const { isLoading: isLoadingAccess, surveyType, hasAccess, permissions } = useProgressReportAccess({
    annualPlanId,
    planOrgId: planOrgIdNum,
    rcnId: rcnIdNum,
    redirectPath: '..'
  })

  const canEdit = permissions.includes('edit')

  const eoyType = surveyType === SurveyType.RCNMidYear
    ? SurveyType.RCNEndOfYear
    : SurveyType.ISDEndOfYear

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (!hasAccess || surveyType == null) return

      const survey = await handleApiResponse(GetSurvey(surveyType, planOrgIdNum))
      if (survey != null) {
        setIsMidYearSubmitted(survey.isSubmitted ?? false)
      }

      if (eoyType != null) {
        const eoySurvey = await handleApiResponse(GetSurvey(eoyType, planOrgIdNum))
        if (eoySurvey != null) {
          setIsEoySubmitted(eoySurvey.isSubmitted ?? false)
        }
      }

      const dates = await handleApiResponse(GetProgressReportDates(annualPlanId))
      if (dates != null) {
        setMidYearDueDate(dates.midYearDueDate)
        setEndOfYearDueDate(dates.endOfYearDueDate)
      }
      setLoading(false)
    }

    void loadData()
  }, [hasAccess, surveyType, eoyType, annualPlanId, planOrgIdNum])

  if (isLoadingAccess) {
    return <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
  }

  return (
    <Card sx={ styles.card }
      data-testid='progressReportsCard'
    >
      {loading
        ? <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
        : <Box sx={ styles.columnBox }>
          <Typography variant='h1'>Progress Reports</Typography>

          <Typography variant='body1'>
            Share information about your progress toward your Implementation Plan with START at Mid-Year and End of Year by completing the Progress Reports.
          </Typography>

          <ProgressReportCard
            title={'Mid-Year Progress Report'}
            date={midYearDueDate}
            description={'Complete the Mid-Year Progress Report to share your progress toward your Implementation Plan with START.'}
            buttonText={isMidYearSubmitted ? 'View Summary' : (canEdit ? 'Complete Report' : 'In Progress')}
            disabled={!isMidYearSubmitted && !canEdit}
            onButtonClick={() => { navigate('MidYear', { state: { surveyType } }) }}
            testId='midYearReport'
          />

          <ProgressReportCard
            title='End of Year Progress Report'
            date={endOfYearDueDate}
            description='Complete the End of Year Progress Report to share your progress toward your Implementation Plan with START.'
            buttonText={isEoySubmitted ? 'View Summary' : (canEdit ? 'Complete Report' : 'In Progress')}
            disabled={(!isEoySubmitted && !canEdit) || eoyType == null}
            onButtonClick={() => { navigate('EndOfYear', { state: { surveyType: eoyType } }) }}
            testId='endYearReport'
          />
        </Box>
      }
    </Card>
  )
}
